/**
 * IndexedDB service for offline data persistence.
 *
 * Stores pending expenses created while offline and caches recent
 * API data so the app remains usable without connectivity.
 */

import type { CreateSplitExpenseDto } from '../types/api';

const DB_NAME = 'fet-offline';
const DB_VERSION = 2;

const STORES = {
  PENDING_EXPENSES: 'pendingExpenses',
  CACHED_DATA: 'cachedData',
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PendingExpenseStatus = 'pending' | 'syncing' | 'error';

export interface PendingExpense {
  localId?: number;
  /** Strongly typed — exact shape the API expects. */
  payload: CreateSplitExpenseDto;
  status: PendingExpenseStatus;
  /** ISO timestamp of when the user created the expense offline. */
  createdAt: string;
  /** Last error message if status === 'error'. */
  lastError?: string;
  /** Number of times sync was attempted. */
  retryCount: number;
}

// ---------------------------------------------------------------------------
// DB helpers
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = event.oldVersion;

      if (oldVersion < 1) {
        db.createObjectStore(STORES.PENDING_EXPENSES, {
          keyPath: 'localId',
          autoIncrement: true,
        });
        db.createObjectStore(STORES.CACHED_DATA, { keyPath: 'key' });
      }

      if (oldVersion < 2) {
        // v2 added status/lastError/retryCount — no structural migration
        // needed since IndexedDB is schema-less per-record, but any v1
        // records will get defaults on read via normalisePendingExpense().
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
): IDBObjectStore {
  return db.transaction(storeName, mode).objectStore(storeName);
}

/**
 * Normalise a raw IDB record into the current PendingExpense shape.
 * Handles records written by DB_VERSION 1 that lack newer fields.
 */
function normalisePendingExpense(raw: Record<string, unknown>): PendingExpense {
  return {
    localId: raw.localId as number | undefined,
    payload: raw.payload as CreateSplitExpenseDto,
    status: (raw.status as PendingExpenseStatus) ?? 'pending',
    createdAt: (raw.createdAt as string) ?? new Date().toISOString(),
    lastError: raw.lastError as string | undefined,
    retryCount: (raw.retryCount as number) ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Pending Expenses (offline queue)
// ---------------------------------------------------------------------------

export async function addPendingExpense(
  payload: CreateSplitExpenseDto,
): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.PENDING_EXPENSES, 'readwrite');
    const item: Omit<PendingExpense, 'localId'> = {
      payload,
      status: 'pending',
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };
    const req = store.add(item);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror = () => reject(req.error);
  });
}

export async function getAllPendingExpenses(): Promise<PendingExpense[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.PENDING_EXPENSES, 'readonly');
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = req.result as Record<string, unknown>[];
      resolve(rows.map(normalisePendingExpense));
    };
    req.onerror = () => reject(req.error);
  });
}

export async function updatePendingExpense(
  item: PendingExpense,
): Promise<void> {
  if (item.localId == null) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.PENDING_EXPENSES, 'readwrite');
    const req = store.put(item);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function removePendingExpense(localId: number): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.PENDING_EXPENSES, 'readwrite');
    const req = store.delete(localId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getPendingExpenseCount(): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.PENDING_EXPENSES, 'readonly');
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Cached Data (generic key-value cache)
// ---------------------------------------------------------------------------

interface CachedEntry {
  key: string;
  data: unknown;
  cachedAt: string;
}

export async function setCachedData(key: string, data: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.CACHED_DATA, 'readwrite');
    const entry: CachedEntry = {
      key,
      data,
      cachedAt: new Date().toISOString(),
    };
    const req = store.put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getCachedData<T = unknown>(
  key: string,
): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.CACHED_DATA, 'readonly');
    const req = store.get(key);
    req.onsuccess = () => {
      const entry = req.result as CachedEntry | undefined;
      resolve(entry ? (entry.data as T) : null);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function clearCachedData(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, STORES.CACHED_DATA, 'readwrite');
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
